/**
 * devices/SharedLoginNote.tsx — the third exit, and the one that is TRUE most
 * of the time.
 *
 * In a B2B exchange the usual cause of a device cap is not three of your own
 * machines. It is TWO HUMANS ON ONE COMPANY LOGIN — the owner and the buyer in
 * the same office — who will now evict each other, all day, forever. The
 * pick-one interstitial "works" perfectly for them and never stops happening,
 * which is the worst kind of broken: no error, no bug report, just a product
 * that is quietly infuriating.
 *
 * Two evictions on this browser inside 24 hours is a strong enough signal to
 * say so out loud. The detection is entirely client-side (a list of epoch ms in
 * localStorage, pruned to 24h) — no backend, no PII, and it degrades to "the
 * note never appears" if storage is unavailable, which is the safe direction.
 *
 * It is deliberately NOT an upsell dressed as an error. It names what is
 * happening, states the fix, and says the first three seats are free — which
 * they are (docs/ORG-ACCOUNTS.md). Nothing here promises a purchase surface
 * that isn't armed yet: while `ORGS_LIVE` is off the CTA is the same human path
 * as the ladder's support exit, so it can never land on a 404.
 */
import { troubleMailto } from './ExitLadder'

export function SharedLoginNote({ uid, deviceId }: { uid: string | null; deviceId: string | null }) {
  return (
    <div
      data-testid="device-gate-shared-login"
      className="mt-5 rounded-md border border-gold-500/35 bg-gold-050/70 px-4 py-3"
    >
      <p className="text-sm font-semibold text-navy-900">Two people are using this sign-in.</p>
      <p className="mt-1 text-[13px] leading-relaxed text-navy-500">
        Diaex allows three sessions per person, not per company. Give your colleague their own
        sign-in and you both stay signed in — the first three are free, and they inherit your
        company’s verification.
      </p>
      <a
        href={troubleMailto({
          uid,
          deviceId,
          subject: 'Adding a colleague to our Diaex account',
        })}
        className="mt-2.5 inline-flex min-h-[44px] items-center text-sm font-semibold text-navy-900 underline underline-offset-4 hover:text-gold-600"
      >
        Ask us to add a colleague
      </a>
    </div>
  )
}
